/* eslint-disable no-console */
import { httpModule } from '@shared/http';
import { Account } from '@services/account';
import { Concept } from '@services/concept';
import { AttachedProof } from '@/assets/entities';
import { Property } from '../property';

export type TransactionDto = {
    amount: number;
    multiplier: number;
    associatedTo: 'Tenant' | 'Propietor' | 'Administrator';
    concept: number;
    account: number;
    property: number;
    date: string;
    description?: string;
    attachProofIds?: number[];
    period?: string;
    includeBilling: boolean;
    associatedPropietor?: number;
    associatedTenant?: number;
    contract?: number;
    imputation?: string;
    organizationAccount?:number;
};

export type TransactionBetweenAccountsDto = {
    rootAccount: number;
    destinationAccount: number;
    amount: number;
    date: string;
    attachProofIds?: number[];
    description: string
}

export type Transaction = {
    id: number;
    date: string;
    amount: number;
    multiplier: number;
    description: string;
    canceled: boolean;
    isDeletable: boolean;
    imputation: string;
    includedInBilling: boolean;
    account: Account;
    concept: Concept;
    attachProofIds: AttachedProof[];
    property: Property;
    associatedTenantName?: string;
    associatedPropietorName?: string;
}

export type FetchOption = 'ById' | 'ByUserId' | 'ByPropertyId' | 'ByAccount'
                            | 'ByAmount' | 'ByDates' | 'ByTenantId' | 'ByContractId' | 'ByPropietorId';

export interface TransactionsFilters {
    type: string;
    minAmount: number;
    maxAmount: number;
    accounts: number[];
    fromDate: string;
    toDate: string;
    concepts: number[];
    properties: number[];
}

export const TransactionsFiltersDefault = (): TransactionsFilters => ({
    type: undefined,
    minAmount: undefined,
    maxAmount: undefined,
    accounts: [],
    fromDate: undefined,
    toDate: undefined,
    concepts: [],
    properties: []
});

export async function fetchAccountTransactions(page: number, limit: number, search?: string, filters?: TransactionsFilters) {
    const {
        type,
        minAmount,
        maxAmount,
        accounts,
        fromDate,
        toDate,
        concepts,
        properties
    } = filters;
    try {
        return await httpModule.get(`/account-transactions`, {
            params: {
                page,
                limit,
                search,
                type,
                minAmount,
                maxAmount,
                accounts,
                fromDate,
                toDate,
                concepts,
                properties
            }
        });
    } catch (ex) {
        console.warn('Error fetching account transactions');
        console.log(ex.response);
    }
}

export async function fetchAccountTransactionsByProperty(id: number, page: number, limit: number, period: string) {
    try {
        return await httpModule.get(`/account-transactions/property/${id}`, {
            params: {
                page: page || 1,
                limit: limit || 100,
                period // || `${(new Date()).getFullYear()}-${(new Date()).getMonth() + 1}`
            }
        });
    } catch (ex) {
        console.warn('Error fetching account transactions');
        console.log(ex.response);
    }
}

export async function fetchAccountTransactionsByOptions(option: FetchOption, id: number, page?: number, limit?: number, period?: string, filters?: TransactionsFilters) {
    const {
        type,
        minAmount,
        maxAmount,
        accounts,
        fromDate,
        toDate,
        concepts,
        properties
    } = filters;
    try {
        return await httpModule.get(`/account-transactions/by-option`, {
            params: {
                option,
                id,
                page,
                limit,
                period,
                type,
                minAmount,
                maxAmount,
                accounts,
                fromDate,
                toDate,
                concepts,
                properties
            }
        });
    } catch (ex) {
        console.warn('Error fetching account transactions');
        console.log(ex.response);
    }
}

export async function postTransaction(propertyTransaction: TransactionDto) {
    try {
        return await httpModule.post(`/account-transactions`, propertyTransaction);
    } catch(ex) {
        console.warn('Error posting property transaction');
        console.log(ex.response);
    }
}

export async function postTransactionBetweenAccounts(propertyTransaction: TransactionBetweenAccountsDto) {
    try {
        return await httpModule.post(`/account-transactions/transfer`, propertyTransaction);
    } catch(ex) {
        console.warn('Error posting property transaction');
        console.log(ex.response);
    }
}

export async function patchAccountStatus(id:number, isDefault:boolean, name?:string, user?:number) {
    try {
        return await httpModule.patch('/accounts', { id, isDefault, name, user });
    } catch (ex) {
        console.warn('Error updating cash Accounts Status');
        console.log(ex.response);
    }
}
export async function deleteAccountTransaction(id:number) {
    try {
        return await httpModule.delete(`account-transactions/${id}`);
    } catch (ex) {
        console.warn('Error deleting transaction');
        console.log(ex.response);
    }
}

export async function addVoucher(id:number, file: File) {
    try {
        const fileForm = new FormData();
        fileForm.append('file', file);
        return await httpModule.put(`/account-transactions/${id}`, fileForm);
    } catch (ex) {
        console.warn('Add voucher error');
        console.log(ex.response);
    }
}


export async function downloadExcelForAccountTransactionBalance(
    page: number,
    limit: number,
    search?: string,
    filters?: TransactionsFilters
) {
    const {
        type,
        minAmount,
        maxAmount,
        accounts,
        fromDate,
        toDate,
        concepts,
        properties
    } = filters;
    try {
        return await httpModule.get(`/account-transactions/export-xlsx-balance`, {
            params: {
                page,
                limit,
                search,
                type,
                minAmount,
                maxAmount,
                accounts,
                fromDate,
                toDate,
                concepts,
                properties
            }
        });
    } catch (ex) {
        console.warn('Error generating excel for account transactions balance');
        console.log(ex.response);
    }
}
